import { Controller } from "@hotwired/stimulus"

// "Refresh from TikTok" on the Trending Sounds page (owner register
// 2026-10-08, SMT_Trending_Sounds_Copy_Plan.md section 4). Every word comes
// from the server (TrendingStatus), so the page and this button can never
// disagree. No line names a vendor or a price (owner 2026-10-08).
//
//   click   -> button disables at once (the double-click guard)
//           -> ask the server what a refresh would do now (refresh X of 3,
//              running already? refused, e.g. an intern inside 8 hours?)
//           -> the in-app confirm, focus on Cancel
//           -> start (the server's lock is the backstop for two tabs or people)
//   then    -> follow the check every 5 seconds until it ends, with no
//              stopwatch: the run record is the truth, not a timer
//
// Text is always set with textContent, never innerHTML.
export default class extends Controller {
  static targets = ["button", "status", "progress", "note", "result", "source"]
  static values = { previewUrl: String, checkUrl: String, statusUrl: String, runId: Number }

  connect() {
    this.failures = 0
    if (this.hasRunIdValue && this.runIdValue) this.follow(this.runIdValue)
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  async start(event) {
    event.preventDefault()
    if (this.buttonTarget.disabled) return
    this.setButton("One moment...", true)
    this.say(this.noteTarget, "")

    let preview
    try {
      preview = await this.getJson(this.previewUrlValue)
    } catch (_) {
      this.say(this.noteTarget, "TEAM didn't answer, so nothing was started. Try again in a minute.", "amber")
      return this.resetButton()
    }

    if (preview.state === "running") {
      this.showRunning(preview)
      return this.follow(null)
    }
    if (preview.state === "refused") return this.block(preview.message)

    const confirmed = await window.appConfirm(preview.message, {
      title: preview.title,
      confirmLabel: preview.confirm_label,
      cancelLabel: "Cancel",
      focus: "cancel"
    })
    if (!confirmed) return this.resetButton()

    this.setButton("Starting...", true)
    let started
    try {
      started = await this.sendJson(this.checkUrlValue)
    } catch (_) {
      this.say(this.noteTarget, "TEAM didn't answer, so nothing was started. Try again in a minute.", "amber")
      return this.resetButton()
    }
    if (started.state === "refused") return this.block(started.message)
    if (started.note) this.say(this.noteTarget, started.note, "grey")
    this.handle(started)
  }

  follow(runId) {
    this.runId = runId
    this.setButton("Refreshing...", true)
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.poll(), 5000)
  }

  async poll() {
    const url = this.runId ? `${this.statusUrlValue}?run_id=${this.runId}` : this.statusUrlValue
    try {
      const data = await this.getJson(url)
      this.failures = 0
      this.handle(data)
    } catch (_) {
      this.failures += 1
      if (this.failures >= 3) {
        // The installed app has no reload button, so the way back is a link.
        this.say(this.progressTarget, "Lost touch with the check. It may still be running.", "amber")
        this.appendReload(this.progressTarget, "Reload the page")
        return this.resetButton()
      }
      this.timer = setTimeout(() => this.poll(), 5000)
    }
  }

  handle(data) {
    if (data.state === "queued" || data.state === "running") {
      this.showRunning(data)
      return this.follow(data.run_id)
    }
    if (data.finish_line) {
      this.say(this.statusTarget, "")
      this.say(this.progressTarget, "")
      this.showResult(data.finish_line, data.finish_level)
    }
    // An intern's 8 hours start when a refresh finishes, so the answer may
    // say the button is now closed to them.
    if (data.button_blocked) return this.block(data.button_note)
    this.resetButton()
  }

  // The server refused (limit used, intern inside 8 hours, paused): say why
  // and leave the button off, as a fresh page load would.
  block(message) {
    this.say(this.noteTarget, message, "amber")
    this.setButton("Refresh from TikTok", true)
  }

  showRunning(data) {
    this.clearSource()
    this.say(this.statusTarget, data.status_line, "accent")
    this.say(this.progressTarget, [data.progress_line, "You can leave this page. The check keeps going."].filter(Boolean).join(" "))
  }

  // The server-rendered "Last check: ..." line describes the previous check;
  // once a new one starts it would contradict the live lines, so it goes.
  clearSource() {
    if (this.hasSourceTarget) this.sourceTarget.remove()
  }

  showResult(text, level) {
    this.clearSource()
    const target = this.resultTarget
    target.textContent = ""
    target.className = this.levelClass(level)
    target.append(document.createTextNode(text))
    this.appendReload(target, "Show the new numbers")
    target.hidden = false
  }

  appendReload(target, label) {
    target.append(document.createTextNode(" "))
    const link = document.createElement("a")
    link.href = window.location.href
    link.textContent = label
    link.className = "underline font-medium py-3"
    target.append(link)
  }

  say(target, text, level) {
    target.textContent = text || ""
    target.hidden = !text
    if (level) target.className = this.levelClass(level)
  }

  levelClass(level) {
    const tone = {
      green: "text-[#3F7A4F]", amber: "text-[#9A6A12]", red: "text-[#B23B3B]",
      accent: "text-[#8B6914]", grey: "text-[#8C8C8C]"
    }[level] || "text-[#8C8C8C]"
    return `text-xs ${tone}`
  }

  setButton(label, disabled) {
    this.buttonTarget.textContent = label
    this.buttonTarget.disabled = disabled
  }

  resetButton() {
    this.setButton("Refresh from TikTok", false)
  }

  async getJson(url) {
    const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  }

  async sendJson(url) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": token },
      credentials: "same-origin"
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return response.json()
  }
}
